<template>
  <div>
    <Head :title="`${form.first_name} ${form.last_name}`" />
    <div class="flex justify-start mb-8 max-w-3xl">
      <h1 class="text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" href="/users">
          Users
        </Link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ form.first_name }} {{ form.last_name }}
      </h1>
      <img
        v-if="user.photo"
        class="block ml-4 w-8 h-8 rounded-full"
        :src="user.photo">
      <span
        v-if="user.is_superadmin"
        class="ml-3 bg-purple-100 text-purple-800 text-xs font-semibold px-2.5 py-0.5 rounded">
        Superadmin
      </span>
    </div>
    <trashed-message
      v-if="user.deleted_at"
      class="mb-6"
      @restore="restore">
      This user has been deleted.
    </trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input
            v-model="form.first_name"
            :error="form.errors.first_name"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="First name" />
          <text-input
            v-model="form.last_name"
            :error="form.errors.last_name"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Last name" />
          <select-input
            v-model="form.employee_id"
            :error="form.errors.employee_id"
            class="pb-8 pr-6 md:w-1/2"
            label="Mapped Employee"
            :disabled="!hasAccess.manage_users">
            <option :value="null" disabled>
              [N/A]
            </option>
            <option
              v-for="employee in employees"
              :key="employee.id"
              :value="employee.id">
              {{ employee.name }}
            </option>
          </select-input>
          <select-input
            v-model="form.owner"
            :error="form.errors.owner"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Owner"
            v-if="hasAccess.manage_users">
            <option :value="true">
              Yes
            </option>
            <option :value="false">
              No
            </option>
          </select-input>
          <div v-if="hasAccess.manage_users" class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="form-label">Roles:</label>
            <div class="mt-1">
              <label
                v-for="role in roles"
                :key="role.id"
                class="flex items-center py-1">
                <input
                  v-model="form.roles"
                  type="checkbox"
                  class="form-checkbox"
                  :value="role.id">
                <span class="ml-2 text-sm">
                  {{ role.name }}
                </span>
              </label>
            </div>
            <div v-if="form.errors.roles" class="form-error">
              {{ form.errors.roles }}
            </div>
          </div>
          <text-input
            v-model="form.email"
            :error="form.errors.email"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Email"
            autocomplete="email"
            type="email" />
          <div class="w-full" />
          <div class="pb-8 pr-6 w-full">
            <label class="flex items-center">
              <input
                v-model="form.change_password"
                type="checkbox"
                class="form-checkbox"
                @change="handlePasswordToggle">
              <span class="ml-2">Change Password</span>
            </label>
          </div>
          <template v-if="form.change_password">
            <text-input
              v-model="form.password"
              :error="form.errors.password"
              class="pb-8 pr-6 w-full lg:w-1/2"
              type="password"
              autocomplete="new-password"
              label="New Password" />
            <text-input
              v-model="form.password_confirmation"
              :error="form.errors.password_confirmation"
              class="pb-8 pr-6 w-full lg:w-1/2"
              type="password"
              autocomplete="new-password"
              label="Confirm New Password" />
          </template>
          <!--file-input
            v-model="form.photo"
            :error="form.errors.photo"
            class="pb-8 pr-6 w-full lg:w-1/2"
            type="file"
            accept="image/*"
            label="Photo" /-->
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button
            v-if="!user.deleted_at && hasAccess.manage_users"
            class="text-red-600 hover:underline"
            tabindex="-1"
            type="button"
            @click="destroy">
            Delete User
          </button>
          <loading-button
            :loading="form.processing"
            class="btn-indigo ml-auto"
            type="submit">
            Update User
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
// import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'

export default {
    components: {
    // FileInput,
        Head,
        Link,
        LoadingButton,
        SelectInput,
        TextInput,
        TrashedMessage,
    },
    layout: Layout,
    props: {
        user: Object,
        employees: Object,
        roles: Array,
    },
    remember: 'form',
    data () {
        return {
            form: this.$inertia.form({
                _method: 'put',
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                email: this.user.email,
                change_password: false,
                password: '',
                password_confirmation: '',
                owner: this.user.owner,
                employee_id: this.user.employee_id,
                roles: this.user.role_ids,
                photo: null,
            }),
        }
    },
    computed: {
        hasAccess () {
            return this.$page.props.auth.user.can
        },
    },
    methods: {
        handlePasswordToggle () {
            if (!this.form.change_password) {
                this.form.password = ''
                this.form.password_confirmation = ''
                this.form.clearErrors('password', 'password_confirmation')
            }
        },
        update () {
            this.form.post(`/users/${this.user.id}`, {
                onSuccess: () => {
                    this.form.reset('password', 'password_confirmation', 'photo')
                    this.form.change_password = false
                },
            })
        },
        destroy () {
            if (confirm('Are you sure you want to delete this user?')) {
                this.$inertia.delete(`/users/${this.user.id}`)
            }
        },
        restore () {
            if (confirm('Are you sure you want to restore this user?')) {
                this.$inertia.put(`/users/${this.user.id}/restore`)
            }
        },
    },
}
</script>
